import {
  ScrollReveal,
  Label,
  Stat,
  Divider,
  Photo,
  ReportFooter,
  containerClassName,
} from "./ReportComponents";
import { PhotoGallery } from './PhotoGallery';
import ConnectionsGame from '../games/ConnectionsGame';
import TriviaGame from '../games/TriviaGame';
import MemoryMatchGame from '../games/MemoryMatchGame';
import ReportGameWrapper from '../games/ReportGameWrapper';
import BudmenLogo from '../shared/BudmenLogo';
import '../../styles/reportStyles.css';
import '../../styles/reports.css';
import '../../styles/reportPrint.css';

// ═══════════════════════════════════════════════════════════════════
// WATTS THE SPOT REPORT COMPONENT
// ═══════════════════════════════════════════════════════════════════

export function WattsReport({ data }) {

  const getCityClassName = (city) => {
    switch(city.toLowerCase()) {
      case 'binghamton': return 'city-profile--binghamton';
      case 'rochester': return 'city-profile--rochester';
      case 'syracuse': return 'city-profile--syracuse';
      default: return '';
    }
  };

  const getCityColorClass = (city) => {
    switch(city.toLowerCase()) {
      case 'binghamton': return 'city-profile__name--teal';
      case 'rochester': return 'city-profile__name--orange';
      case 'syracuse': return 'city-profile__name--purple';
      default: return 'city-profile__name--teal';
    }
  };

  const getBulletColorClass = (city) => {
    switch(city.toLowerCase()) {
      case 'binghamton': return 'city-profile__bullet--teal';
      case 'rochester': return 'city-profile__bullet--orange';
      case 'syracuse': return 'city-profile__bullet--purple';
      default: return 'city-profile__bullet--teal';
    }
  };

  return (
    <div className="report-container">
      {/* HERO */}
      <div
        className="report-hero"
        style={data.heroImage ? {
          backgroundImage: `url(${data.heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        } : undefined}
      >
        {data.heroImage ? (
          <div className={`report-hero__overlay report-hero__overlay--${data.heroOverlay || 'dark'}`} />
        ) : (
          <div className="report-hero__pattern" />
        )}
        <div className="report-hero__content">
          <div className="report-hero__logo">
            <BudmenLogo fill="white" height="40" />
          </div>
          <div className="report-hero__main">
            <Label color="var(--gold)">Program Report</Label>
            <h1 className="report-hero__title">
              {data.program.name}
            </h1>
            <p className="report-hero__tagline">
              {data.program.tagline}
            </p>
          </div>
          <div className="report-hero__meta">
            <span>{data.program.partner}</span>
            <span>·</span>
            <span>{data.program.locations}</span>
            <span>·</span>
            <span>{data.program.dates}</span>
          </div>
        </div>
      </div>

      {/* DRIVING QUESTION */}
      <ScrollReveal className="report-section--driving">
        <div className={containerClassName}>
          <Label>The Driving Question</Label>
          <blockquote className="report-blockquote">
            {data.drivingQuestion}
          </blockquote>
        </div>
      </ScrollReveal>

      {/* OVERVIEW */}
      <ScrollReveal className="report-section--overview">
        <div className={containerClassName}>
          <p className="report-text">
            {data.overview}
          </p>
        </div>
      </ScrollReveal>

      {/* STATS */}
      <ScrollReveal className="report-stats">
        <div className="report-stats__container">
          <Stat number={data.program.studentsServed} label="Students Served" />
          <Stat number="3" label="City Cohorts" />
          <Stat number={data.program.sessionsDelivered} label="Sessions Delivered" />
          <Stat number={data.program.hoursOfInstruction} label="Hours of Instruction" />
        </div>
      </ScrollReveal>

      <Divider />

      {/* CITY PROFILES */}
      <ScrollReveal className="report-section--tight">
        <div className={containerClassName}>
          <Label>City Profiles</Label>
          <h2 className="report-h2">
            Three Cities, Three Characters
          </h2>

          {data.cityProfileIntro && (
            <p className="report-text">
              {data.cityProfileIntro}
            </p>
          )}

          {/* City Profiles - show content first */}
          <div className="city-profiles">
            {data.cityProfiles.map((city, index) => (
                <ScrollReveal key={city.city} delay={index * 100}>
                  <div className={`city-profile ${getCityClassName(city.city)}`}>
                    <h3 className="city-profile__theme">
                      {city.theme}
                    </h3>
                    <div className="city-profile__header">
                      <span className={`city-profile__name ${getCityColorClass(city.city)}`}>
                        {city.city}
                      </span>
                      <span className="city-profile__students">
                        {city.studentsCount || `~${data.program.cityCohorts[index].students} students`}
                      </span>
                    </div>
                    <p className="city-profile__summary">
                      {city.summary}
                    </p>
                    <div className="city-profile__highlights">
                      {city.highlights.map((highlight, j) => (
                        <div key={j} className="city-profile__highlight">
                          <div className={`city-profile__bullet ${getBulletColorClass(city.city)}`} />
                          <span className="city-profile__highlight-text">
                            {highlight}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>

          {/* Connections Game - comes after reading the profiles */}
          {data.connectionsGame && (
            <ReportGameWrapper
              showProgress={false}
              theme={{
                primaryColor: '#1A8A7D',
                secondaryColor: '#F7C948',
                borderColor: '#e5e7eb'
              }}
            >
              <ConnectionsGame
                title="Discover the Connections"
                instructions="Sort these sixteen items into four groups"
                data={data.connectionsGame}
              />
            </ReportGameWrapper>
          )}
        </div>
      </ScrollReveal>

      {/* PHOTO GRID */}
      <ScrollReveal className="report-section--overview photo-section">
        <div className={containerClassName}>
          <Label>In the Room</Label>
          {data.images && data.images.length > 0 ? (
            <PhotoGallery images={data.images} />
          ) : (
            <>
              <div className="photo-grid">
                <Photo caption="Binghamton students testing piezo components on stairs" />
                <Photo caption="Rochester team mapping social factors on whiteboard" />
                <Photo caption="The Syracuse duck Power Beacon in all its glory" />
              </div>
              <div className="photo-grid--single">
                <Photo
                  caption="All three city cohorts at their respective Learning Showcases"
                  aspect="2.2/1"
                />
              </div>
            </>
          )}
        </div>
      </ScrollReveal>

      <Divider />

      {/* OBSERVATIONS */}
      <ScrollReveal className="report-section--tight">
        <div className={containerClassName}>
          <Label>Facilitator Observations</Label>
          <h2 className="report-h2">
            Key Insights From 21 Sessions
          </h2>

          {data.facilitatorObservationsIntro && (
            <p className="report-text">
              {data.facilitatorObservationsIntro}
            </p>
          )}

          {/* Facilitator Observations - show content first */}
          {data.facilitatorObservations && (
            <div className="observations">
              {data.facilitatorObservations.map((obs, i) => (
                <ScrollReveal key={i} delay={i * 70}>
                  <div className="observation-card">
                    <h3 className="observation-card__theme">
                      {obs.theme}
                    </h3>
                    <p className="observation-card__text">
                      {obs.observation}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          )}

          {/* Trivia Game - comes after reading the observations */}
          {data.triviaGame && (
            <ReportGameWrapper
              showProgress={false}
              theme={{
                primaryColor: '#F5A623',
                secondaryColor: '#1A8A7D',
                borderColor: '#e5e7eb'
              }}
            >
              <TriviaGame
                title="Test Your Knowledge"
                instructions="How well do you know what happened?"
                data={data.triviaGame}
              />
            </ReportGameWrapper>
          )}
        </div>
      </ScrollReveal>

      <Divider />

      {/* IMPROVEMENTS */}
      {data.improvements && (
        <>
          <ScrollReveal className="report-section--tight">
            <div className={containerClassName}>
              <Label color="var(--rust)">Continuous Improvement</Label>
              <h2 className="report-h2">
                How We're Evolving
              </h2>
              <p className="report-text--small">
                Part of the Creative Discovery Method is turning the lens on ourselves.
                Here's what we learned about improving the program.
              </p>
              <div className="improvements">
                {data.improvements.map((improvement, i) => (
                  <ScrollReveal key={i} delay={i * 80}>
                    <div className="improvement-card">
                      <h3 className="improvement-card__area">
                        {improvement.area}
                      </h3>
                      <p className="improvement-card__detail">
                        {improvement.detail}
                      </p>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </ScrollReveal>
          <Divider />
        </>
      )}

      {/* OUTCOMES */}
      <ScrollReveal className="report-section--tight">
        <div className={containerClassName}>
          <Label>Outcomes</Label>
          <h2 className="report-h2">
            Student Capabilities
          </h2>

          {data.outcomesIntro && (
            <p className="report-text">
              {data.outcomesIntro}
            </p>
          )}

          {/* Outcomes - show content first */}
          {data.outcomes && (
            <div className="outcomes">
              {data.outcomes.map((outcome, i) => (
                <ScrollReveal key={i} delay={i * 40}>
                  <div className="outcome">
                    <div className="outcome__bullet" />
                    <p className="outcome__text">
                      {outcome}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          )}

          {/* Memory Match Game - comes after reading the outcomes */}
          {data.memoryMatchGame && (
            <ReportGameWrapper
              showProgress={false}
              theme={{
                primaryColor: '#C4652A',
                secondaryColor: '#F7C948',
                borderColor: '#e5e7eb'
              }}
            >
              <MemoryMatchGame
                title="Match the Outcomes"
                instructions="Connect each outcome to its source"
                data={data.memoryMatchGame}
              />
            </ReportGameWrapper>
          )}
        </div>
      </ScrollReveal>

      <Divider />

      {/* WHAT'S NEXT */}
      <ScrollReveal className="report-section--tight">
        <div className={containerClassName}>
          <Label>Looking Ahead</Label>
          <h2 className="report-h2">
            The Path Forward
          </h2>
          {data.lookingAhead ? (
            <>
              <p className="report-text">
                {data.lookingAhead.intro}
              </p>
              <div className="looking-ahead-actions">
                {data.lookingAhead.actions.map((action, index) => (
                  <div key={index} className="looking-ahead-action">
                    <span className="looking-ahead-action__number">{index + 1}.</span>
                    <p className="looking-ahead-action__text">{action}</p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="report-text">
              {data.whatsNext}
            </p>
          )}
        </div>
      </ScrollReveal>

      {/* FOOTER */}
      <ReportFooter reportDate={data.program.reportDate} partner={data.program.partner} />
    </div>
  );
}